import { Component, DoCheck, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import {
  OrdersPaymentRequest,
  OrdersPaymentRequestStatusEnum
} from '~/lazy/payments/pages/new-payment/models/ordersPaymentRequest';
import { ReplaySubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { PaymentMapper } from '~/modules/payments/payment.mapper';
import {
  createOrdersPaymentAskForExecution,
  createOrdersPaymentCanceled,
  createOrdersPaymentSuccessUserHasRead,
  loadPaymentRecentPayments,
  loadPaymentRecentTransfers,
  selectCurrentRequest
} from '~/modules/payments/slice';
import { selectAppDeepLinkPaymentConfirmationUrlScheme } from '~/modules/app/slice';
import { redirectedAfterAuthenticationSuccess } from '@qvs/core/auth';
import { ValidatePaymentOrderRequestSuccessComponent } from '../../components/validate-payment-order-request-success/validate-payment-order-request-success.component';
import { ValidatePaymentOrderRequestFailedComponent } from '../../components/validate-payment-order-request-failed/validate-payment-order-request-failed.component';
import { ValidatePaymentOrderRequestUploadingComponent } from '../../components/validate-payment-order-request-uploading/validate-payment-order-request-uploading.component';
import { ValidatePaymentOrderRequestProceedComponent } from '../../components/validate-payment-order-request-proceed/validate-payment-order-request-proceed.component';
import { NgIf } from '@angular/common';
import { SpinnerCenteredComponent } from '~/shared/components/spinner-centered/spinner-centered.component';

@Component({
  selector: 'qvs-validate-payment-order-request',
  templateUrl: './validate-payment-order-request.page.html',
  styleUrls: ['./validate-payment-order-request.page.scss'],
  standalone: true,
  imports: [
    NgIf,
    ValidatePaymentOrderRequestProceedComponent,
    ValidatePaymentOrderRequestUploadingComponent,
    ValidatePaymentOrderRequestFailedComponent,
    ValidatePaymentOrderRequestSuccessComponent,
    SpinnerCenteredComponent
  ]
})
export class ValidatePaymentOrderRequestPage implements OnInit, OnDestroy, DoCheck {
  @Output() canDismiss: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();
  currentPaymentRequest: OrdersPaymentRequest;
  status = OrdersPaymentRequestStatusEnum;
  isReturningFromDeepLink = false;
  readonly destroyed$ = new Subject<void>();

  constructor(private readonly store: Store) {}

  public get readyToSubmit(): boolean {
    return this.currentPaymentRequest?.status === this.status.readyToSubmit;
  }

  public get loading(): boolean {
    return this.running && this.isReturningFromDeepLink;
  }

  public get running(): boolean {
    return PaymentMapper.isOrdersPaymentRequestRunning(this.currentPaymentRequest);
  }

  public get success(): boolean {
    return PaymentMapper.isOrdersPaymentRequestRegistrationSuccessful(this.currentPaymentRequest);
  }

  public get failed(): boolean {
    return PaymentMapper.isOrdersPaymentRequestRegistrationFailed(this.currentPaymentRequest);
  }

  ngOnInit() {
    this.init();
  }

  ngOnDestroy() {
    if (!this.loading) {
      if (this.readyToSubmit || this.running) {
        this.store.dispatch(createOrdersPaymentCanceled({ ordersPaymentRequest: this.currentPaymentRequest }));
      } else if (this.success) {
        this.store.dispatch(createOrdersPaymentSuccessUserHasRead({ ordersPaymentRequest: this.currentPaymentRequest }));
      } else if (this.failed) {
        if (!this.currentPaymentRequest) {
          this.store.dispatch(redirectedAfterAuthenticationSuccess());
        }
        if (!this.currentPaymentRequest.status) {
          this.store.dispatch(createOrdersPaymentAskForExecution({ ordersPaymentRequest: this.currentPaymentRequest }));
        } else {
          this.store.dispatch(createOrdersPaymentCanceled({ ordersPaymentRequest: this.currentPaymentRequest }));
        }
      }
    }
    this.destroyed$.next();
  }

  ngDoCheck() {
    this.canDismiss.next(
      !(!this.loading && this.running && this.currentPaymentRequest.status !== OrdersPaymentRequestStatusEnum.userSubmitFailure)
    );
  }

  close() {
    this.closed.emit();
  }

  private init() {
    this.store
      .select(selectCurrentRequest)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((currentPaymentRequest) => {
        this.currentPaymentRequest = currentPaymentRequest;
        if (this.success || this.failed) {
          this.store.dispatch(loadPaymentRecentPayments({ loadOnlyIfEmpty: false }));
          this.store.dispatch(loadPaymentRecentTransfers());
        }
      });

    this.store
      .select(selectAppDeepLinkPaymentConfirmationUrlScheme)
      .pipe(
        takeUntil(this.destroyed$),
        filter((result) => !!result)
      )
      .subscribe(() => (this.isReturningFromDeepLink = true));
  }
}
